// Library Methods
import { useMemo } from "react";

// The four depth-cue targets, arranged in a diamond: top, left, right, bottom -- same order as
// optionIndex's "Option_1".."Option_4". The catch ("none stood out") target sits apart, below the
// diamond, where the "?" is printed.
const POSITIONS = [
  { x: 0, y: -25.91 },
  { x: -25.91, y: 0 },
  { x: 25.91, y: 0 },
  { x: 0, y: 25.91 },
];
const CATCH_POSITION = { x: 0, y: 66 };
const CATCH_INDEX = 4;

const RING_RADIUS = 17.33;
const CATCH_RING_RADIUS = 10.23;

const GOOD = "#22BE39";
const BAD = "#E10C0C";
const BACKGROUND = "#808080";
const PICTURE_HEIGHT = "225px";
const FRAME_DARK = "#2A3150";
const FRAME_LIGHT = "#D7D8D4";
const FONT_FAMILY = "'Gill Sans MT', 'Gill Sans', Calibri, sans-serif";

const positionFor = (index: number) =>
  index === CATCH_INDEX ? CATCH_POSITION : POSITIONS[index] ?? null;

const ringRadiusFor = (index: number) =>
  index === CATCH_INDEX ? CATCH_RING_RADIUS : RING_RADIUS;

type TrialPictureType = {
  seed: number;
  expectedIndex: number;
  responseIndex: number;
  correct: boolean;
};

// What the patient actually saw -- which one of the four targets has stereo depth -- can't be
// shown in a flat image, so all four render identically. The number in the middle and the rings
// marking the answer/pick are this review tool's own annotations, not part of the stimulus.
const TrialPicture = ({
  seed,
  expectedIndex,
  responseIndex,
  correct,
}: TrialPictureType) => {
  const variant = useMemo(() => seed, [seed]);

  const expectedPosition = positionFor(expectedIndex);
  const responsePosition = positionFor(responseIndex);

  return (
    <svg
      viewBox="-60 -56 120 138"
      role="img"
      aria-label={`Diamond ${variant}`}
      // A fixed height so circle and shape trials line up; the background is painted on the svg
      // itself so it fills the full box even where the drawing letterboxes inside it.
      style={{
        display: "block",
        width: "100%",
        height: PICTURE_HEIGHT,
        backgroundColor: BACKGROUND,
        borderRadius: "8px",
      }}
    >
      <rect
        x={-41.205}
        y={-41.205}
        width={82.41}
        height={82.41}
        rx={19.97}
        transform="rotate(45)"
        fill={FRAME_DARK}
      />
      <rect
        x={-39.39}
        y={-39.39}
        width={78.78}
        height={78.78}
        rx={18.15}
        transform="rotate(45)"
        fill={FRAME_LIGHT}
      />
      <rect
        x={-37.74}
        y={-37.74}
        width={75.48}
        height={75.48}
        rx={16.5}
        transform="rotate(45)"
        fill={FRAME_DARK}
      />

      {POSITIONS.map(({ x, y }, index) => (
        <g key={index}>
          <circle cx={x} cy={y} r={15.35} fill={FRAME_LIGHT} />
          <circle cx={x} cy={y} r={9.21} fill={FRAME_DARK} />
          <circle cx={x} cy={y} r={4.452} fill={FRAME_LIGHT} />
        </g>
      ))}

      <text
        x={0}
        y={0}
        fontSize={15.2}
        fontWeight={600}
        fill={FRAME_LIGHT}
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily={FONT_FAMILY}
      >
        {variant}
      </text>
      <text
        x={0}
        y={66}
        fontSize={15.675}
        fontWeight={600}
        fill={FRAME_LIGHT}
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily={FONT_FAMILY}
      >
        ?
      </text>

      {correct
        ? responsePosition && (
            <circle
              cx={responsePosition.x}
              cy={responsePosition.y}
              r={ringRadiusFor(responseIndex)}
              fill="none"
              stroke={GOOD}
              strokeWidth={3}
            />
          )
        : [
            expectedPosition && (
              <circle
                key="expected"
                cx={expectedPosition.x}
                cy={expectedPosition.y}
                r={ringRadiusFor(expectedIndex)}
                fill="none"
                stroke={GOOD}
                strokeWidth={2.4}
                strokeDasharray="4 3"
              />
            ),
            responsePosition && (
              <circle
                key="response"
                cx={responsePosition.x}
                cy={responsePosition.y}
                r={ringRadiusFor(responseIndex)}
                fill="none"
                stroke={BAD}
                strokeWidth={3}
              />
            ),
          ]}
    </svg>
  );
};

export default TrialPicture;
